import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { toast } from 'react-toastify';
import { toInteger } from 'lodash';

import {
  SC_GET_CURRENT_WORKSPACE,
  SC_GET_WORKSPACES,
  WM_CREATE_WORKSPACE_MEMBER,
  WM_GET_WORKSPACE_ROLES,
  WM_SEARCH_WORKSPACE_MEMBER_BY_EMAIL
} from '../../apollo/api';
import useWorkspaceId from '../../hooks/useWorkspaceId';
import {
  Mode as AddWorkspaceModalMode,
  AddWorkspaceMemberModal
} from '../../features/workspace-member/molecules';
import { getWhiteLabelWebsite } from '../../helpers/white-label-helper';
import { handleError } from '../../core';

const AddWorkspaceMember: FC<{ isOpen: boolean; onCancel?: () => void; shouldShowSkip?: boolean;}> = ({ isOpen, onCancel, shouldShowSkip }) => {
  const currentWorkspace = useRef(null);

  const [selectedWorkspaceMember, setSelectedWorkspaceMember] = useState(null);
  const [searchEmail, setSearchEmail] = useState<string>('');
  const [isAddWorkspaceMemberLoading, setIsAddWorkspaceMemberLoading] = useState<boolean>(false);
  const [isOpenAddWorkspaceMemberModal, setIsOpenAddWorkspaceMemberModal] = useState(isOpen);

  const workspaceId = useWorkspaceId();

  const {
    data: searchWorkspaceMemberByEmail,
    refetch: refetchSearchWorkspaceMemberByEmail
  } = useQuery(WM_SEARCH_WORKSPACE_MEMBER_BY_EMAIL, {
    variables: {
      email: searchEmail,
    }
  });

  const { data: currentWorkspaceData } = useQuery(SC_GET_CURRENT_WORKSPACE, {
    variables: {
      workspaceId,
    },
    skip: !workspaceId
  });
  const { data: workspaceData } = useQuery(SC_GET_WORKSPACES);
  const { data: workspaceRoles } = useQuery(WM_GET_WORKSPACE_ROLES);

  const [createWorkspaceMember] = useMutation(WM_CREATE_WORKSPACE_MEMBER);

  const setDefaultAddWorkSpaceMemberModalStatus = useCallback(() => {
    setIsAddWorkspaceMemberLoading(false);
    setSelectedWorkspaceMember(null);
    setIsOpenAddWorkspaceMemberModal(false);
  }, [
    setIsAddWorkspaceMemberLoading,
    setSelectedWorkspaceMember,
    setIsOpenAddWorkspaceMemberModal,
  ]);

  const handleOnAddWorkspaceMember = useCallback(
    async (data: any) => {
      setIsAddWorkspaceMemberLoading(true);
      const { email, workspaceRoleId, firstName, lastName } = data;
      try {
        const {
          data: {
            createWorkspaceMember: { success, message }
          }
        } = await createWorkspaceMember({
          variables: {
            workspaceId: currentWorkspace.current?.id,
            email,
            workspaceRoleId: toInteger(workspaceRoleId),
            firstName,
            lastName,
            domain: getWhiteLabelWebsite()
          }
        });

        if (success) {
          toast.success('Workspace Member was successfully created');
        } else {
          toast.error(message);
        }

        return;
      } catch (error) {
        return handleError({
          error,
          userFriendlyMessage: 'Error occurred. Workspace Member was not created.'
        });
      } finally {
        setDefaultAddWorkSpaceMemberModalStatus();
      }
    },
    [createWorkspaceMember, currentWorkspace.current?.id, setDefaultAddWorkSpaceMemberModalStatus]
  );

  const handleSubmit = useCallback((data) => {
    handleOnAddWorkspaceMember(data);
  }, [handleOnAddWorkspaceMember]);

  const handleClose = useCallback(() => {
    setDefaultAddWorkSpaceMemberModalStatus();

    onCancel && onCancel();
  }, [
    onCancel,
    setDefaultAddWorkSpaceMemberModalStatus
  ]);

  useEffect(() => {
    if (searchWorkspaceMemberByEmail) {
      const { searchWorkspaceMemberByEmail: data } = searchWorkspaceMemberByEmail
      setSelectedWorkspaceMember({ user: data })
    }
  }, [searchWorkspaceMemberByEmail]);

  useEffect(() => {
    currentWorkspace.current =
      workspaceId && currentWorkspaceData?.workspace
        ? currentWorkspaceData?.workspace
        : workspaceData?.workspaces[0];
  }, [workspaceId, currentWorkspaceData?.workspace, workspaceData?.workspaces[0]]);

  return (
    <AddWorkspaceMemberModal
      data={selectedWorkspaceMember}
      isOpen={isOpenAddWorkspaceMemberModal}
      loading={isAddWorkspaceMemberLoading}
      mode={AddWorkspaceModalMode.ADD}
      mutationError={false}
      onClose={handleClose}
      onSubmit={handleSubmit}
      setSearchEmail={setSearchEmail}
      workspaceName={currentWorkspace.current?.name}
      workspaceRoles={workspaceRoles?.workspaceRoles || null}
      shouldShowSkip={shouldShowSkip}
    />
  );
};

export default AddWorkspaceMember;